import { useState } from "react";
import { motion } from "framer-motion";
import { TrainFront } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

export default function PnrLookup() {
  const [pnr, setPnr] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const check = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(pnr)) {
      toast.error("Enter a valid 10-digit PNR number");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/railway/pnr/${pnr}`);
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="pnr" className="bg-emerald-950 py-24 lg:py-32" data-testid="pnr-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mx-auto mb-12 max-w-2xl text-center"
        >
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.24em] text-amber-400">Journey tools</p>
          <h2 className="font-display text-4xl font-bold tracking-tight text-amber-50 sm:text-5xl" data-testid="pnr-heading">
            Check your <span className="italic text-amber-400">PNR status</span>
          </h2>
          <p className="mt-4 text-base text-amber-100/70 sm:text-lg">
            Confirm your coach and berth, then tell us exactly where to find you.
          </p>
        </motion.div>

        <form onSubmit={check} className="mx-auto flex max-w-xl flex-col gap-3 sm:flex-row" data-testid="pnr-form">
          <input
            value={pnr}
            onChange={(e) => setPnr(e.target.value.replace(/\D/g, "").slice(0, 10))}
            inputMode="numeric"
            placeholder="Enter 10-digit PNR"
            data-testid="pnr-input"
            className="h-14 flex-1 rounded-full border border-amber-100/20 bg-white/10 px-6 text-lg font-semibold tracking-[0.2em] text-amber-50 placeholder:font-normal placeholder:tracking-normal placeholder:text-amber-100/40 focus:outline-none focus:ring-2 focus:ring-amber-500"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="pnr-check-button"
            className="h-14 rounded-full bg-amber-500 px-8 text-base font-bold text-emerald-950 transition-colors hover:bg-amber-400 disabled:opacity-50"
          >
            {loading ? "Checking…" : "Check Status"}
          </button>
        </form>

        {result && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mx-auto mt-10 max-w-2xl rounded-3xl border border-amber-100/15 bg-white/5 p-7 backdrop-blur"
            data-testid="pnr-result"
          >
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <TrainFront className="h-6 w-6 text-amber-400" />
                <div>
                  <p className="font-display text-xl font-bold text-amber-50" data-testid="pnr-train-name">
                    {result.train.number} · {result.train.name}
                  </p>
                  <p className="text-sm text-amber-100/60">
                    {result.boarding_at} → {result.destination}
                  </p>
                </div>
              </div>
              {result.demo && (
                <span className="rounded-full bg-amber-500/20 px-3 py-1 text-xs font-bold uppercase tracking-widest text-amber-300" data-testid="pnr-demo-badge">
                  Demo data
                </span>
              )}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {result.passengers.map((p) => (
                <div key={p.number} className="rounded-2xl bg-emerald-900/60 p-4" data-testid={`pnr-passenger-${p.number}`}>
                  <p className="text-xs font-bold uppercase tracking-widest text-amber-100/50">Passenger {p.number}</p>
                  <p className="mt-1 font-semibold text-amber-50">{p.booking_status}</p>
                </div>
              ))}
              <div className="rounded-2xl bg-emerald-900/60 p-4" data-testid="pnr-chart-status">
                <p className="text-xs font-bold uppercase tracking-widest text-amber-100/50">Chart status</p>
                <p className="mt-1 font-semibold text-amber-50">{result.chart_status}</p>
              </div>
            </div>
            <p className="mt-4 text-xs text-amber-100/50">{result.note}</p>
          </motion.div>
        )}
      </div>
    </section>
  );
}
